import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { collection, doc } from "firebase/firestore";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { db } from "@/lib/firebase";
import { getOne, createItem, updateItem } from "@/lib/adminRepository";
import ImageUploader from "@/components/admin/ImageUploader";
import { ADMIN } from "@/constants/testIds";

const BLANK = {
  name: "", role: "", quote: "", date: "",
  image: "", featured: true, order: 99, status: "published",
};

const Label = ({ children }) => <span className="editorial-label">{children}</span>;
const Hint = ({ children }) => <span className="block text-[11px] text-olive/80 mb-1">{children}</span>;

export default function TestimonialForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // Stable doc id so avatar/photo upload paths are valid before the first save.
  const docId = useMemo(() => (isNew ? doc(collection(db, "testimonials")).id : id), [isNew, id]);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOne("testimonials", id);
        if (cancelled) return;
        if (data) setForm({ ...BLANK, ...data });
        else toast.error("Testimonial not found.");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[testimonial] load failed:", err);
        toast.error("Failed to load testimonial.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isNew, id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v && v.target ? v.target.value : v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Client name is required.");
    if (!form.quote.trim()) return toast.error("Quote is required.");
    setSaving(true);
    try {
      const { id: _id, ...rest } = form;
      const payload = { ...rest, order: Number(form.order) || 99 };
      if (isNew) {
        await createItem("testimonials", payload, docId);
        toast.success("Testimonial created.");
      } else {
        await updateItem("testimonials", id, payload);
        toast.success("Testimonial saved.");
      }
      navigate("/admin/testimonials");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[testimonial] save failed:", err);
      toast.error("Save failed: " + (err?.message || "error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-olive">Loading…</p>;

  return (
    <div data-testid="admin-testimonial-form">
      <Link to="/admin/testimonials" className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark mb-5">
        <ArrowLeft size={12} /> Back to testimonials
      </Link>
      <header className="mb-7">
        <div className="eyebrow text-bronze mb-1">{isNew ? "New" : "Edit"}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-ink">{isNew ? "New Testimonial" : form.name || "Edit testimonial"}</h1>
      </header>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr,300px] gap-6">
        {/* Main */}
        <div className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <Label>Client name *</Label>
                <input className="editorial-input" value={form.name} onChange={set("name")} data-testid="testimonial-name" placeholder="Jane Tan" />
              </label>
              <label className="block">
                <Label>Role / descriptor</Label>
                <input className="editorial-input" value={form.role} onChange={set("role")} data-testid="testimonial-role" placeholder="HDB Upgrader" />
              </label>
            </div>
            <label className="block">
              <Label>Date published</Label>
              <Hint>Drives the order — newest appears first on the site and the list.</Hint>
              <input type="date" className="editorial-input" value={form.date} onChange={set("date")} data-testid="testimonial-date" />
            </label>
            <label className="block">
              <Label>Quote *</Label>
              <textarea rows={5} className="editorial-input resize-y" value={form.quote} onChange={set("quote")} data-testid="testimonial-quote" placeholder="Their words verbatim — shown in quotes on the site." />
            </label>
          </section>

          <section className="border border-bronze/20 bg-cream p-6">
            <div className="eyebrow text-bronze mb-1">Photo</div>
            <p className="text-xs text-olive mb-3">Portrait shown on the testimonial card.</p>
            <ImageUploader path={`media/testimonials/${docId}/avatar`} value={form.image ? [form.image] : []} onChange={(urls) => set("image")(urls[0] || "")} single testid="testimonial-avatar" />
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <label className="block">
              <Label>Visibility</Label>
              <select className="editorial-input" value={form.status} onChange={set("status")} data-testid="testimonial-status">
                <option value="published">published</option>
                <option value="draft">draft</option>
              </select>
            </label>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-ink">Featured</div>
                <div className="text-[11px] text-olive mt-0.5">Include in homepage carousel</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={form.featured}
                onClick={() => set("featured")(!form.featured)}
                data-testid="testimonial-featured"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.featured ? "bg-bronze" : "bg-taupe/50"}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-cream shadow transition-transform ${form.featured ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            <label className="block">
              <Label>Sort tiebreak</Label>
              <Hint>Used when two testimonials share a date.</Hint>
              <input type="number" className="editorial-input" value={form.order} onChange={set("order")} data-testid="testimonial-order" />
            </label>
          </section>

          <div className="flex gap-3">
            <Link to="/admin/testimonials" className="btn-secondary flex-1 text-center">Cancel</Link>
            <button type="submit" disabled={saving} data-testid={ADMIN.saveButton} className="btn-primary flex-1 justify-center disabled:opacity-60">
              <Save size={14} /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
